﻿class InteractionBehavior {
  constructor() {
    this.onInteraction = null;
    this.isInteractive = false;
    /**
     * This event/callback property should be assigned by the user to a method which understands the
     * UI and which UI events to listen to. toggleInteractive is called from within the activateInteraction
     * method in subclasses.
     */
    this.toggleInteractive = this.toggleInteractiveNull; // Must be replaced by user
  }

  /**
   * Turns interaction on or off for this object. This is the main "public" method for toggling
   * the interactivity. It is called from the application when the app's interactivity is
   * toggled, but also called internally in this class when we update the edit scope.
   * @param {bool} on Whether to turn interaction on or off.
   */
  setIsInteractive(on) {
    if (on !== this.isInteractive) {
      this.activateInteraction(on);
      this.isInteractive = on;
    }
  }

  /**
   * This method should be overridden by subclasses to turn the interaction on/off on the
   * target display objects. This method is used when we need to temporarily turn off
   * interaction while making internal changes. Internally the method might use the toggleInteractive
   * callback to delegate the event add/removal to a class which understands the UI better (e.g.
   * the DrawingEngine subclasses).
   * @param {any} on Activate? or deactivate?
   */
  activateInteraction(on) {
  }

  toggleInteractiveNull(displayObject, on, clickHandler) {
    throw Error('The toggleInteractive callback must be assigned by the owner');
  }

  /**
   * Protected method called from the method which received the UI event. Raises the onInteraction event.
   */
  doOnInteraction(event) {
    if (this.onInteraction) {
      this.onInteraction(this, event);
    }
  }
}

export class ProductInteraction extends InteractionBehavior {
  constructor() {
    super();
    this._product = null;
    this.selectedValue = null;
    this._editScope = null; // Should be assigned by subclass
    this._componentType = null; // Should be assigned by subclass
  }

  get product() {
    return this._product;
  }
  set product(value) {
    if (value !== this._product) {
      if (this.isInteractive) {
        this.activateInteraction(false);
        this._product = value;
        this.activateInteraction(true);
      } else {
        this._product = value;
      }
    }
  }

  /**
   * The edit scope determines whether you're only updating a single component of the product, or
   * multiple components at a certain level. For instance, in a door front if the edit scope is
   * 'door' and the component type is 'panel', then you're updating all panels on the clicked door.
   * Similarly, in an organizer, if the scope is 'module' and the component type is 'side', then all
   * flexi sides in the clicked module are updated.
   */
  get editScope() {
    return this._editScope;
  }
  set editScope(value) {
    if (value !== this._editScope) {
      if (this.isInteractive) {
        this.activateInteraction(false);
        this._editScope = value;
        this.activateInteraction(true);
      } else {
        this._editScope = value;
      }
    }
  }

/**
 * The component type determines which parts of the product you are updating. For instance, in a
 * door front if the edit scope is 'door' and the component type is 'panel', then you're updating
 * all panels on the clicked door. Similarly, in an organizer, if the scope is 'module' and the
 * component type is 'side', then all flexi sides in the clicked module are updated.
 */
  get componentType() {
    return this._componentType;
  }
  set componentType(value) {
    if (value !== this._componentType) {
      if (this.isInteractive) {
        this.activateInteraction(false);
        this._componentType = value;
        this.activateInteraction(true);
      } else {
        this._componentType = value;
      }
    }
  }

  /**
   * Removes the clickHandler from the old display object, and adds it to the new display object,
   * but only if interaction is enabled on the old, and the new display object is different from
   * the old.
   */
  refreshInteractive(oldDisplayObject, newDisplayObject, clickHandler) {
    if (oldDisplayObject.interactive && newDisplayObject !== oldDisplayObject) {
      // The displayObject was recreated, we must make it interactive again:
      this.toggleInteractive(newDisplayObject, true, clickHandler);
      // Clean up
      this.toggleInteractive(oldDisplayObject, false);
    }
  }

  doOnProductChanged(changedComponent) {
    if (this.onProductChanged) {
      this.onProductChanged(this, this.componentType, changedComponent);
    }
  }

  unsupportedTypeAndScope() {
    const message = `Unsupported component type (${this.componentType.id}) and edit scope (${this.editScope}) combination`;
    // In the prototype app we can't throw exceptions due to how the UI works
    //throw new Error(message);
    console.warn(message);
  }

  triggerDrawingClick() {
    const event = {
      target: { dataComponent: this.product }
    };
    if (this.componentType) {
      this.componentType.handleDrawingClick(this, event);
    } else {
      this.unsupportedTypeAndScope();
    }
  }
}

export class AnyProductInteraction extends ProductInteraction {
  constructor() {
    super();
    /* We need to create a wrapper object for the removeEventListener call to actually remove the
     * listener. This happens inside the drawing engine's toggleDisplayObjectInteractive method. */
    this.productClickHandler = { handler: event => this.handleProductClick(event) };
  }

  activateInteraction(on) {
    super.activateInteraction(on);
    if (this.product) {
      /* Passing an arrow function to the method may prevent the toggleInteractive method from
        * successfully unregistering the event. Instead we use a wrapper object. */
      //this.toggleInteractive(this.product.displayObject, on, (event) => this.handleProductClick(event));
      this.toggleInteractive(this.product.displayObject, on, this.productClickHandler.handler);
    }
  }

  handleProductClick(event) {
    //const productDisplayObject = event.currentTarget;
    this.doOnInteraction(event);
  }
}
